import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import {
  AlertTriangle,
  ArrowLeftRight,
  ArrowDownToLine,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  Info,
  Map as MapIcon,
  Minus,
  PackageMinus,
  Play,
  Plus,
  ShieldAlert,
  TrendingDown,
  TrendingUp,
  Truck,
} from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  BarTrend,
  Gauge,
  Heatmap,
  Sparkline,
  TrendChart,
} from "@/components/charts/Charts";
import { deltaPct, movingAverage, rate } from "@/components/charts/scale";
import { Badge, Card, type StatusTone } from "@/components/ui";
import {
  COUNT_CAP,
  homeSummaryQuery,
  homeTrendsQuery,
  trendPeriods,
  type AttentionKind,
  type BacklogMetric,
  type HomeSummary,
  type HomeTrends,
  type MovementType,
  type RateMetric,
  type TrendPeriod,
} from "@/features/home/api";
import { useActiveCompany } from "@/lib/activeCompany";
import { cn } from "@/lib/cn";
import { todayIso, useFormat } from "@/lib/format";
import { firstNameOf, useSessionContext } from "@/lib/session";
import { isDemoMode } from "@/lib/supabase";

// Home (docs/design/home.md): two RPCs in parallel — home_summary (counts,
// attention, latest movements; refetched every minute) and home_trends (daily
// KPIs of the chosen period from kpi_daily). Trend cards with the change
// against the previous period, the flow of goods, service-level gauges,
// backlog trend and the warehouse rhythm. Charts are hand-drawn SVG; every
// skeleton matches the final layout.

export const Route = createFileRoute("/_app/")({
  validateSearch: (raw: Record<string, unknown>): { p?: TrendPeriod } => {
    const p = Number(raw.p);
    return p === 7 || p === 90 ? { p } : {};
  },
  component: HomePage,
});

const attentionStyle: Record<
  AttentionKind,
  { icon: LucideIcon; tone: string; to: string }
> = {
  overdue_receipts: {
    icon: Clock,
    tone: "bg-(--status-warning-bg) text-(--status-warning-fg)",
    to: "/procurement/purchase-orders",
  },
  late_shipments: {
    icon: Truck,
    tone: "bg-(--status-warning-bg) text-(--status-warning-fg)",
    to: "/sales/orders",
  },
  material_shortages: {
    icon: AlertTriangle,
    tone: "bg-(--status-danger-bg) text-(--status-danger-fg)",
    to: "/manufacturing/production-orders",
  },
  purchase_drafts: {
    icon: CheckCircle2,
    tone: "bg-accent-soft text-accent-ink",
    to: "/procurement/purchase-orders",
  },
  counts_in_progress: {
    icon: ClipboardCheck,
    tone: "bg-(--mod-warehousing-soft) text-(--mod-warehousing-ink)",
    to: "/warehousing/cycle-counts",
  },
  below_safety: {
    icon: PackageMinus,
    tone: "bg-(--status-danger-bg) text-(--status-danger-fg)",
    to: "/planning/stock-coverage",
  },
  inspections_open: {
    icon: ClipboardCheck,
    tone: "bg-(--mod-quality-soft) text-(--mod-quality-ink)",
    to: "/quality/inspection-orders",
  },
  ncr_open: {
    icon: ShieldAlert,
    tone: "bg-(--status-danger-bg) text-(--status-danger-fg)",
    to: "/quality/nonconformances",
  },
};

const movementTone: Record<MovementType, StatusTone> = {
  purchase_receipt: "success",
  production_receipt: "success",
  adjustment_in: "info",
  transfer_in: "info",
  transfer_out: "info",
  production_issue: "violet",
  sales_issue: "warning",
  adjustment_out: "warning",
  count_adjustment: "neutral",
};

function HomePage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data: session } = useSessionContext();
  const { company } = useActiveCompany();
  const enabled = !!company || isDemoMode;
  const summary = useQuery({ ...homeSummaryQuery(company?.id ?? ""), enabled });
  const period: TrendPeriod = search.p ?? 30;
  const trends = useQuery({
    ...homeTrendsQuery(company?.id ?? "", period),
    enabled,
    placeholderData: keepPreviousData,
  });
  const data = summary.data;
  const tr = trends.data;
  const now = new Date();
  const hour = now.getHours();
  const greetingKey =
    hour < 12
      ? "home.greetingMorning"
      : hour < 18
        ? "home.greetingAfternoon"
        : "home.greetingEvening";
  const count = (n: number) => (n > COUNT_CAP ? `${COUNT_CAP}+` : String(n));
  const labels = useMemo(
    () =>
      tr
        ? tr.flows.received.series.map((_, i) =>
            fmt.day(addDaysIso(tr.from, i)),
          )
        : [],
    [tr, fmt],
  );

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col">
          <span className="text-[13px] text-ink-muted first-letter:uppercase">
            {fmt.longDay(now)}
          </span>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">
            {session
              ? t(greetingKey, {
                  name: firstNameOf(session.user.full_name, session.user.email),
                })
              : " "}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          {tr && (
            <span
              className="text-xs text-ink-muted"
              title={
                tr.refreshed_at ? fmt.dateTime(tr.refreshed_at) : undefined
              }
            >
              {tr.refreshed_at
                ? t("home.updated", { time: fmt.time(tr.refreshed_at) })
                : t("home.preparing")}
            </span>
          )}
          <div
            role="radiogroup"
            aria-label={t("home.period")}
            className="flex rounded-xl border border-border bg-surface p-0.5"
          >
            {trendPeriods.map((p) => (
              <button
                key={p}
                type="button"
                role="radio"
                aria-checked={period === p}
                onClick={() =>
                  void navigate({
                    search: p === 30 ? {} : { p },
                    replace: true,
                  })
                }
                className={cn(
                  "h-8 rounded-[10px] px-3 text-[13px] font-medium tabular-nums transition-colors",
                  period === p
                    ? "bg-ink text-surface"
                    : "text-ink-muted hover:bg-surface-hover hover:text-ink",
                )}
              >
                {t("home.periodDays", { n: p })}
              </button>
            ))}
          </div>
        </div>
      </header>

      {isDemoMode && (
        <p className="flex items-center gap-2 self-start rounded-lg bg-surface-muted px-3 py-1.5 text-[13px] text-ink-muted">
          <Info size={14} aria-hidden="true" />
          {t("home.demoNotice")}
        </p>
      )}

      <section
        aria-label={t("home.quickActions")}
        className="flex flex-wrap gap-2.5"
      >
        <QuickAction to="/procurement/purchase-orders" icon={Plus} primary>
          {t("home.newPurchaseOrder")}
        </QuickAction>
        <QuickAction
          to="/procurement/purchase-orders"
          icon={ArrowDownToLine}
          iconClass="text-(--mod-warehousing-ink)"
        >
          {t("home.receiveGoods")}
        </QuickAction>
        <QuickAction
          to="/manufacturing/production-orders"
          icon={Play}
          iconClass="text-(--mod-manufacturing-ink)"
        >
          {t("home.startProduction")}
        </QuickAction>
        <QuickAction
          to="/warehousing/transfers"
          icon={ArrowLeftRight}
          iconClass="text-(--mod-sales-ink)"
        >
          {t("home.transferStock")}
        </QuickAction>
        <QuickAction to="/process-map" icon={MapIcon} iconClass="text-accent-ink">
          {t("nav.settingsGroup.processMap")}
        </QuickAction>
      </section>

      {(summary.isError || trends.isError) && !(data && tr) && (
        <p role="alert" className="text-[13px] text-(--status-danger-fg)">
          {t("home.loadFailed")}{" "}
          <button
            type="button"
            onClick={() => {
              void summary.refetch();
              void trends.refetch();
            }}
            className="font-medium underline"
          >
            {t("home.retry")}
          </button>
        </p>
      )}

      <section
        aria-label={t("home.glance")}
        className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4"
      >
        <TrendCard
          to="/sales/shipments"
          label={t("home.cardShipped")}
          flow={tr?.flows.shipped}
          color="var(--mod-sales-ink)"
          period={period}
          hint={
            data && t("home.hintOpenSo", { n: data.kpis.open_sales_orders })
          }
        />
        <TrendCard
          to="/procurement/receipts"
          label={t("home.cardReceived")}
          flow={tr?.flows.received}
          color="var(--mod-procurement-ink)"
          period={period}
          hint={
            data &&
            t("home.hintOpenPo", {
              n: data.kpis.open_purchase_orders,
              drafts: data.kpis.purchase_drafts,
            })
          }
        />
        <TrendCard
          to="/manufacturing/production-orders"
          label={t("home.cardProduced")}
          flow={tr?.flows.produced}
          color="var(--mod-manufacturing-ink)"
          period={period}
          hint={
            data && t("home.hintProduction", { n: data.kpis.production_active })
          }
        />
        <TrendCard
          to="/sales/orders"
          label={t("home.cardNewOrders")}
          flow={tr?.flows.so_new}
          color="var(--accent)"
          period={period}
          hint={tr && t("home.hintPoReleased", { n: tr.flows.po_new.total })}
        />
      </section>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card
          aria-labelledby="flow-h"
          className="flex flex-col gap-3 p-4 xl:col-span-2"
        >
          <CardTitle
            id="flow-h"
            title={t("home.flowTitle")}
            subtitle={t("home.flowSubtitle", { n: period })}
          />
          {tr ? (
            <>
              <TrendChart
                ariaLabel={t("home.flowAria", { n: period })}
                hiddenLabel={t("home.seriesHidden")}
                shownLabel={t("home.seriesShown")}
                labels={labels}
                format={fmt.count}
                series={[
                  {
                    key: "received",
                    label: t("home.flowReceived"),
                    color: "var(--mod-procurement-ink)",
                    values: tr.flows.received.series,
                  },
                  {
                    key: "produced",
                    label: t("home.flowProduced"),
                    color: "var(--mod-manufacturing-ink)",
                    values: tr.flows.produced.series,
                  },
                  {
                    key: "shipped",
                    label: t("home.flowShipped"),
                    color: "var(--mod-sales-ink)",
                    values: tr.flows.shipped.series,
                  },
                ]}
              />
              <dl className="grid grid-cols-3 gap-2 border-t border-border-subtle pt-3">
                {(["consumed", "transferred", "adjusted"] as const).map((m) => (
                  <MiniStat
                    key={m}
                    label={t(`home.flow_${m}`)}
                    value={tr.flows[m].total}
                    prev={tr.flows[m].prev}
                  />
                ))}
              </dl>
            </>
          ) : (
            <div
              aria-hidden="true"
              className="h-[300px] animate-pulse rounded-xl bg-surface-muted"
            />
          )}
        </Card>

        <Card aria-labelledby="service-h" className="flex flex-col gap-3 p-4">
          <CardTitle
            id="service-h"
            title={t("home.serviceTitle")}
            subtitle={t("home.serviceSubtitle")}
          />
          <div className="grid grow grid-cols-2 content-center gap-x-2 gap-y-5">
            {serviceRates.map(({ key, target }) => {
              const r = tr?.rates[key];
              const v = r ? rate(r.value, r.base) : null;
              const pv = r ? rate(r.prev_value, r.prev_base) : null;
              const d =
                v !== null && pv !== null
                  ? Math.round((v - pv) * 10) / 10
                  : null;
              return r ? (
                <Gauge
                  key={key}
                  value={v}
                  target={target}
                  label={t(`home.rate_${key}`)}
                  valueText={v === null ? "—" : `${fmt.pct(v)}%`}
                >
                  <span className="text-[11px] text-ink-muted">
                    {r.base > 0
                      ? t("home.rateOf", {
                          value: fmt.count(r.value),
                          base: fmt.count(r.base),
                        })
                      : t("home.rateNone")}
                  </span>
                  <span className="text-[11px] text-ink-faint">
                    {t("home.target", { value: target })}
                    {d !== null && d !== 0 && (
                      <span
                        className={cn(
                          "ml-1 font-medium",
                          d > 0
                            ? "text-(--status-success-fg)"
                            : "text-(--status-danger-fg)",
                        )}
                      >
                        {d > 0 ? "▲" : "▼"} {fmt.pct(Math.abs(d))}{" "}
                        {t("home.pp")}
                      </span>
                    )}
                  </span>
                </Gauge>
              ) : (
                <div
                  key={key}
                  aria-hidden="true"
                  className="mx-auto h-24 w-28 animate-pulse rounded-xl bg-surface-muted"
                />
              );
            })}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Attention data={data} />
        <Backlog trends={tr} summary={data} count={count} />
        <Rhythm trends={tr} />
      </div>

      <Movements data={data} />
    </div>
  );
}

const serviceRates: { key: RateMetric; target: number }[] = [
  { key: "otd", target: 95 },
  { key: "supplier_otd", target: 95 },
  { key: "schedule", target: 90 },
  { key: "fpy", target: 98 },
];

function addDaysIso(iso: string, n: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const x = new Date(y!, m! - 1, d! + n);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
}

function CardTitle({
  id,
  title,
  subtitle,
  action,
}: {
  id: string;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2">
      <div className="flex grow flex-col">
        <h2 id={id} className="text-base font-semibold">
          {title}
        </h2>
        {subtitle && <span className="text-xs text-ink-muted">{subtitle}</span>}
      </div>
      {action}
    </div>
  );
}

function Delta({
  current,
  previous,
  period,
}: {
  current: number;
  previous: number;
  period: number;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const d = deltaPct(current, previous);
  if (d === null)
    return (
      <span className="text-xs text-ink-faint">{t("home.noPrevious")}</span>
    );
  const up = d > 0;
  const flat = d === 0;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 rounded-full px-1.5 text-xs font-semibold leading-5",
        flat
          ? "bg-surface-muted text-ink-muted"
          : up
            ? "bg-(--status-success-bg) text-(--status-success-fg)"
            : "bg-(--status-danger-bg) text-(--status-danger-fg)",
      )}
      title={t("home.vsPrevious", { n: period })}
    >
      {flat ? (
        <Minus size={12} aria-hidden="true" />
      ) : up ? (
        <TrendingUp size={12} aria-hidden="true" />
      ) : (
        <TrendingDown size={12} aria-hidden="true" />
      )}
      {up ? "+" : ""}
      {fmt.pct(d)}%
      <span className="sr-only"> {t("home.vsPrevious", { n: period })}</span>
    </span>
  );
}

function TrendCard({
  to,
  label,
  flow,
  color,
  period,
  hint,
}: {
  to: string;
  label: string;
  flow: { series: number[]; total: number; prev: number } | undefined;
  color: string;
  period: number;
  hint: string | undefined;
}) {
  const fmt = useFormat();
  return (
    <Link
      to={to}
      className="group flex flex-col gap-1 overflow-hidden rounded-2xl border border-border bg-surface pt-4 transition-colors hover:bg-surface-hover"
    >
      <span className="flex items-center gap-2 px-4 text-[13px] text-ink-muted">
        <span
          aria-hidden="true"
          className="size-2 rounded-full"
          style={{ background: color }}
        />
        {label}
      </span>
      {flow ? (
        <span className="flex items-baseline gap-2 px-4">
          <span className="tabular text-[30px] font-semibold leading-9 tracking-[-0.02em]">
            {fmt.count(flow.total)}
          </span>
          <Delta current={flow.total} previous={flow.prev} period={period} />
        </span>
      ) : (
        <span
          aria-hidden="true"
          className="mx-4 my-1 block h-7 w-24 animate-pulse rounded bg-surface-muted"
        />
      )}
      {hint === undefined ? (
        <span
          aria-hidden="true"
          className="mx-4 block h-4 w-28 animate-pulse rounded bg-surface-muted"
        />
      ) : (
        <span className="px-4 text-xs text-ink-muted">{hint}</span>
      )}
      {/* 30 / 90 days: a 7-day moving average (from the 7th day, no warm-up), so the line shows the trend rather than the weekends. */}
      <span className="mt-2">
        {flow ? (
          <Sparkline
            values={
              period > 7 ? movingAverage(flow.series, 7).slice(6) : flow.series
            }
            zero={period === 7}
            color={color}
            height={44}
          />
        ) : (
          <span className="block h-11" />
        )}
      </span>
    </Link>
  );
}

function MiniStat({
  label,
  value,
  prev,
}: {
  label: string;
  value: number;
  prev: number;
}) {
  const fmt = useFormat();
  const d = deltaPct(value, prev);
  return (
    <div className="flex flex-col">
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd className="flex items-baseline gap-1.5">
        <span className="tabular text-lg font-semibold">
          {fmt.count(value)}
        </span>
        {d !== null && d !== 0 && (
          <span
            className={cn(
              "text-xs font-medium",
              d > 0 ? "text-ink-soft" : "text-ink-muted",
            )}
          >
            {d > 0 ? "+" : ""}
            {fmt.pct(d)}%
          </span>
        )}
      </dd>
    </div>
  );
}

const backlogRows: { key: BacklogMetric; to: string; color: string }[] = [
  { key: "late_so", to: "/sales/orders", color: "var(--status-warning-fg)" },
  {
    key: "late_po",
    to: "/procurement/purchase-orders",
    color: "var(--status-warning-fg)",
  },
  {
    key: "late_mo",
    to: "/manufacturing/production-orders",
    color: "var(--status-danger-fg)",
  },
  {
    key: "short_mo",
    to: "/manufacturing/production-orders",
    color: "var(--status-danger-fg)",
  },
];

function Backlog({
  trends,
  summary,
  count,
}: {
  trends: HomeTrends | undefined;
  summary: HomeSummary | undefined;
  count: (n: number) => string;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const current = (key: BacklogMetric): number | null => {
    if (summary) {
      if (key === "late_so") return summary.kpis.late_sales_orders;
      if (key === "late_po") return summary.kpis.receipts_overdue;
      if (key === "short_mo") return summary.kpis.production_short;
    }
    const s = trends?.backlog[key];
    return s ? (s[s.length - 1] ?? null) : null;
  };
  const firstDay = trends
    ? trends.backlog.late_so.findIndex((v) => v !== null)
    : -1;
  return (
    <Card aria-labelledby="backlog-h" className="flex flex-col gap-3 p-4">
      <CardTitle
        id="backlog-h"
        title={t("home.backlogTitle")}
        subtitle={
          trends && firstDay > 0
            ? t("home.backlogSince", {
                day: fmt.day(addDaysIso(trends.from, firstDay)),
              })
            : t("home.backlogSubtitle")
        }
      />
      <ul className="flex flex-col">
        {backlogRows.map(({ key, to, color }) => {
          const v = current(key);
          const series = trends?.backlog[key];
          return (
            <li key={key}>
              <Link
                to={to}
                className="-mx-2 flex items-center gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-surface-hover"
              >
                <span className="flex min-w-0 grow flex-col">
                  <span className="text-[13px] font-medium">
                    {t(`home.backlog_${key}`)}
                  </span>
                  <span className="w-full max-w-[180px]">
                    {series ? (
                      <BarTrend values={series} color={color} height={22} />
                    ) : (
                      <span className="block h-[22px]" />
                    )}
                  </span>
                </span>
                {v === null ? (
                  <span
                    aria-hidden="true"
                    className="h-6 w-8 animate-pulse rounded bg-surface-muted"
                  />
                ) : (
                  <span
                    className={cn(
                      "tabular text-2xl font-semibold",
                      v > 0 ? "" : "text-ink-faint",
                    )}
                  >
                    {count(v)}
                  </span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function Rhythm({ trends }: { trends: HomeTrends | undefined }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => fmt.weekday(i)),
    [fmt],
  );
  const peak = useMemo(() => {
    if (!trends) return null;
    let best = { d: 0, h: 0, v: 0 };
    trends.heatmap.forEach((row, d) =>
      row.forEach((v, h) => v > best.v && (best = { d, h, v })),
    );
    return best.v > 0 ? best : null;
  }, [trends]);
  const total = trends ? trends.heatmap.flat().reduce((a, b) => a + b, 0) : 0;
  return (
    <Card aria-labelledby="rhythm-h" className="flex flex-col gap-3 p-4">
      <CardTitle
        id="rhythm-h"
        title={t("home.rhythmTitle")}
        subtitle={t("home.rhythmSubtitle")}
      />
      {trends ? (
        <>
          <Heatmap
            matrix={trends.heatmap}
            dayLabels={days}
            ariaLabel={
              peak
                ? t("home.rhythmAria", {
                    total: fmt.count(total),
                    day: days[peak.d] ?? "",
                    hour: peak.h,
                  })
                : t("home.rhythmEmpty")
            }
            cellTitle={(d, h, v) =>
              t("home.rhythmCell", { day: days[d] ?? "", hour: h, n: v })
            }
          />
          <p className="text-xs text-ink-muted">
            {peak
              ? t("home.rhythmPeak", {
                  day: days[peak.d] ?? "",
                  hour: peak.h,
                  n: peak.v,
                })
              : t("home.rhythmEmpty")}
          </p>
        </>
      ) : (
        <div
          aria-hidden="true"
          className="h-40 animate-pulse rounded-xl bg-surface-muted"
        />
      )}
    </Card>
  );
}

function Attention({ data }: { data: HomeSummary | undefined }) {
  const { t } = useTranslation();
  return (
    <Card aria-labelledby="attention-h" className="p-1.5">
      <div className="flex items-center px-3 pb-2 pt-3">
        <h2 id="attention-h" className="grow text-base font-semibold">
          {t("home.attention")}
        </h2>
        {data && data.attention.length > 0 && (
          <span className="rounded-full bg-accent-soft px-2 text-xs font-semibold leading-5 text-accent-ink">
            {data.attention.length}
          </span>
        )}
      </div>
      {!data ? (
        <ul aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <li key={i} className="flex items-center gap-3 px-3 py-2.5">
              <span className="size-[34px] shrink-0 animate-pulse rounded-[10px] bg-surface-muted" />
              <span className="flex grow flex-col gap-1.5">
                <span className="h-4 w-48 animate-pulse rounded bg-surface-muted" />
                <span className="h-3.5 w-64 animate-pulse rounded bg-surface-muted" />
              </span>
            </li>
          ))}
        </ul>
      ) : data.attention.length === 0 ? (
        <p className="flex items-center gap-2 px-3 pb-4 pt-1 text-[13px] text-ink-muted">
          <CheckCircle2
            size={16}
            aria-hidden="true"
            className="text-(--status-success-fg)"
          />
          {t("home.allClear")}
        </p>
      ) : (
        <ul>
          {data.attention.map((a) => {
            const s = attentionStyle[a.kind];
            const Icon = s.icon;
            const refs = [
              ...(a.refs ?? []),
              a.count > (a.refs?.length ?? 0) ? "…" : null,
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <li key={a.kind}>
                <Link
                  to={s.to}
                  className="flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-surface-hover"
                >
                  <span
                    className={cn(
                      "flex size-[34px] shrink-0 items-center justify-center rounded-[10px]",
                      s.tone,
                    )}
                  >
                    <Icon size={17} strokeWidth={1.9} aria-hidden="true" />
                  </span>
                  <span className="flex min-w-0 grow flex-col">
                    <span className="font-medium">
                      {t(`home.att.${a.kind}`, { count: a.count })}
                    </span>
                    <span className="truncate font-mono text-xs text-ink-muted">
                      {refs}
                      {a.detail ? (
                        <span className="font-sans"> · {a.detail}</span>
                      ) : null}
                    </span>
                  </span>
                  <span className="whitespace-nowrap text-[13px] font-medium text-accent-ink">
                    {t(`home.attAction.${a.kind}`)} →
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function Movements({ data }: { data: HomeSummary | undefined }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const today = todayIso();
  const time = (iso: string) => {
    const d = new Date(iso);
    const local = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return local === today
      ? d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
      : fmt.dateTime(iso);
  };
  return (
    <Card aria-labelledby="movements-h" className="p-1.5">
      <div className="flex items-center px-3 pb-2 pt-3">
        <h2 id="movements-h" className="grow text-base font-semibold">
          {t("home.movements")}
        </h2>
        <Link
          to="/warehousing/inventory"
          search={{ wh: "", q: "" }}
          className="text-[13px] font-medium text-accent-ink hover:underline"
        >
          {t("home.viewAll")}
        </Link>
      </div>
      <div className="overflow-x-auto">
        <table className="tabular w-full border-collapse text-[13px]">
          <thead>
            <tr className="text-left text-ink-muted">
              <th scope="col" className="px-3 py-1.5 font-medium">
                {t("home.colTime")}
              </th>
              <th scope="col" className="px-3 py-1.5 font-medium">
                {t("home.colItem")}
              </th>
              <th scope="col" className="px-3 py-1.5 font-medium">
                {t("home.colType")}
              </th>
              <th scope="col" className="px-3 py-1.5 text-right font-medium">
                {t("home.colQty")}
              </th>
            </tr>
          </thead>
          <tbody>
            {!data ? (
              [0, 1, 2, 3, 4].map((i) => (
                <tr
                  key={i}
                  aria-hidden="true"
                  className="border-t border-border-subtle"
                >
                  {[12, 48, 20, 16].map((w, k) => (
                    <td key={k} className="px-3 py-2.5">
                      <span
                        className="block h-4 animate-pulse rounded bg-surface-muted"
                        style={{ width: `${w * 4}px` }}
                      />
                    </td>
                  ))}
                </tr>
              ))
            ) : data.movements.length === 0 ? (
              <tr className="border-t border-border-subtle">
                <td colSpan={4} className="px-3 py-4 text-ink-muted">
                  {t("home.noMovements")}
                </td>
              </tr>
            ) : (
              data.movements.map((m) => {
                const positive = Number(m.qty) > 0;
                return (
                  <tr key={m.id} className="border-t border-border-subtle">
                    <td className="whitespace-nowrap px-3 py-2.5 text-ink-muted">
                      {time(m.posted_at)}
                    </td>
                    <td className="max-w-0 truncate px-3 py-2.5">
                      <span className="font-mono text-xs">{m.item_code}</span>{" "}
                      {m.item_description}
                      <span className="ml-1.5 font-mono text-xs text-ink-muted">
                        {m.warehouse} · {m.location}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5">
                      <Badge tone={movementTone[m.type]}>
                        {t(`movementType.${m.type}`)}
                      </Badge>
                      {m.reversal && (
                        <span className="ml-1.5 text-xs text-ink-muted">
                          {t("home.reversal")}
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right font-medium">
                      {positive ? "+" : ""}
                      {fmt.qtyIn(m.qty, m.uom_decimals)}{" "}
                      <span className="font-normal text-ink-muted">
                        {m.uom}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function QuickAction({
  to,
  icon: Icon,
  iconClass,
  primary,
  children,
}: {
  to: string;
  icon: LucideIcon;
  iconClass?: string;
  primary?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className={cn(
        "inline-flex h-11 items-center gap-2 rounded-xl pl-3 pr-4 font-medium transition-colors",
        primary
          ? "bg-accent text-on-accent hover:bg-accent-hover"
          : "border border-border bg-surface text-ink hover:bg-surface-hover",
      )}
    >
      <Icon
        size={18}
        strokeWidth={2}
        aria-hidden="true"
        className={primary ? undefined : iconClass}
      />
      {children}
    </Link>
  );
}
